import React, {useState} from 'react';
import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';

import {Box, Button, Card, FormControl, Typography} from '@mui/material';
import logo from '../../livezone_logo.png?inline';
import {FormContainer, TextFieldElement, TextFieldElementProps, ControllerProps, FieldValues, SubmitHandler} from 'react-hook-form-mui';

const LOGIN_QUERY = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      access_token
      refresh_token
      user {
        name
        role {
          name
        }
      }
    }
  }
`;

const TextInput = (props: TextFieldElementProps & {minLength?: number}) => {
  const {minLength, label, ...rest} = props;
  const validation = React.useMemo(() => {
    const value: ControllerProps<FieldValues>['rules'] = {};
    if (minLength) {
      value.minLength = {value: minLength, message: `${label} should be at least ${minLength} characters`};
    }

    return value;
  }, [minLength, label]);
  return <TextFieldElement margin="normal" label={label} rules={validation} {...rest} />;
};

type LoginPayloadType = {
  email: string;
  password: string;
};

type LoginResult = {
  login: {
    access_token: string;
  };
};

const LoginPage = () => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const [error, setError] = React.useState<string>('');

  const [login] = useMutation<LoginResult>(LOGIN_QUERY, {
    onCompleted: (data) => {
      localStorage.setItem('access_token', data.login.access_token);
      setIsSuccess(true);
    },
    onError: (error) => {
      if (error.message) {
        setError(error.message);
      } else {
        setError('Network error.');
      }
    }
  });

  const onSubmit: SubmitHandler<LoginPayloadType> = (payload) => {
    login({
      variables: payload
    });
  };

  if (isSuccess) {
    return (
      <Box className="event-shell" sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Card sx={{ height: 'auto', px: 5, py: 4 }}>
          <Typography sx={{ fontWeight: 700 }}>Signed in</Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="event-shell" sx={{ alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ height: 'auto', width: '100%', maxWidth: 440, borderRadius: '16px' }}>
        <Box
          sx={{
            my: 5,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box component="img" src={logo} alt="LiveZone" sx={{ height: 32, mb: 2 }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Sign in
          </Typography>
            <FormContainer onSuccess={onSubmit}>
              <TextInput required fullWidth name="email" label="Email" autoFocus />
              <TextInput required fullWidth type="password" name="password" label="Password" />
            <FormControl fullWidth sx={{mt: 3, mb: 2, alignItems: 'center'}}>
              <Button type="submit" variant="contained" sx={{ width: '100%', height: 42, fontWeight: 700, borderRadius: '10px' }}>
                Sign in
              </Button>
            </FormControl>
              <Typography color="error">{error}</Typography>
            </FormContainer>
          </Box>
        </Card>
    </Box>
  );
};

export default LoginPage;
